import { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'dark' | 'light' | 'system';

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
}

interface ThemeProviderState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  systemTheme: 'dark' | 'light';
  resolvedTheme: 'dark' | 'light';
  isLoading: boolean;
}

const initialState: ThemeProviderState = {
  theme: 'system',
  setTheme: () => null,
  systemTheme: 'light',
  resolvedTheme: 'light',
  isLoading: false,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
  children,
  defaultTheme = 'system',
  storageKey = 'crowdfpl-web-theme',
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(defaultTheme);
  const [systemTheme, setSystemTheme] = useState<'dark' | 'light'>('light');
  const [isLoading] = useState(false);

  // Вычисляем resolvedTheme внутри компонента
  const resolvedTheme = theme === 'system' ? systemTheme : theme;

  // Определяем системную тему
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const updateSystemTheme = (e?: MediaQueryListEvent) => {
      const isDark = e ? e.matches : mediaQuery.matches;
      setSystemTheme(isDark ? 'dark' : 'light');
    };

    updateSystemTheme();

    // Слушаем изменения системной темы
    mediaQuery.addEventListener('change', updateSystemTheme);
    return () => mediaQuery.removeEventListener('change', updateSystemTheme);
  }, []);

  // Загружаем тему из localStorage после монтирования
  useEffect(() => {
    try {
      const storedTheme = localStorage.getItem(storageKey) as Theme;
      if (storedTheme && ['dark', 'light', 'system'].includes(storedTheme)) {
        setTheme(storedTheme);
      }
    } catch (error) {
      console.warn('Ошибка при загрузке темы из localStorage:', error);
    }
  }, [storageKey]);

  // Применяем тему к документу
  useEffect(() => {
    const root = window.document.documentElement;
    const body = window.document.body;

    // Удаляем предыдущие классы
    root.classList.remove('light', 'dark');
    body.classList.remove('light', 'dark');

    // Добавляем новый класс к html и body
    root.classList.add(resolvedTheme);
    body.classList.add(resolvedTheme);

    // Устанавливаем data-theme атрибут
    root.setAttribute('data-theme', resolvedTheme);
    body.setAttribute('data-theme', resolvedTheme);

    // Принудительно применяем CSS переменные
    if (resolvedTheme === 'dark') {
      root.style.colorScheme = 'dark';
    } else {
      root.style.colorScheme = 'light';
    }

    console.log('Тема применена:', {
      theme,
      resolvedTheme,
      systemTheme,
      htmlClasses: root.className,
      bodyClasses: body.className,
    });

    // Принудительно обновляем отображение
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 0);
  }, [theme, systemTheme, resolvedTheme]);

  const value = {
    theme,
    setTheme: (newTheme: Theme) => {
      try {
        localStorage.setItem(storageKey, newTheme);
        console.log('🎨 Смена темы:', { от: theme, к: newTheme, resolvedTheme });
        setTheme(newTheme);
      } catch (error) {
        console.warn('Ошибка при сохранении темы:', error);
        setTheme(newTheme); // Все равно устанавливаем тему, даже если не удалось сохранить
      }
    },
    systemTheme,
    resolvedTheme,
    isLoading,
  };

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);

  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
};
